/**
 * CSV serialization helpers with spreadsheet formula-injection protection.
 *
 * Untrusted application data (roster names, labor categories, emails) must never
 * become an executable formula when an exported CSV is opened in Excel, Google
 * Sheets or LibreOffice. Neutralization happens here, at the serialization
 * boundary, so it also covers values that were stored before this control
 * existed. Stored data is never rewritten.
 *
 * Used by: server-side CSV exports (Contract Roster).
 */

/**
 * Characters that make a spreadsheet application treat a cell as a formula when
 * they appear first. `\t` and `\r` are included because they lead the classic
 * DDE payloads (`\t=cmd|' /C calc'!A0`).
 */
export const CSV_FORMULA_TRIGGER_CHARS = ['=', '+', '-', '@', '\t', '\r'] as const;

/**
 * Characters we refuse outright at the *input* boundary.
 *
 * Deliberately NOT the same set as {@link CSV_FORMULA_TRIGGER_CHARS}: `-` is
 * missing because "-Smith" is a legitimate surname and a negative number is
 * legitimate business data. Rejecting those at input would break real records
 * for no gain — the output escape neutralizes a leading `-` anyway.
 *
 * The two sets differ on purpose. Keep them side by side so the difference
 * stays visible to whoever changes one of them.
 */
export const CSV_REJECTED_INPUT_PREFIX_CHARS = ['=', '+', '@'] as const;

/**
 * Shown when a submitted value reads as a formula. Lives here so the browser
 * form and the server schema reject with the same words — the UI cannot import
 * `@access-cms/validation`, which carries fastify as a peer dependency.
 */
export const CSV_FORMULA_REJECTION_MESSAGE = 'Value cannot start with =, + or @, or contain control characters';

/** Highest C0 control code point, and the DEL code point. */
const LAST_C0_CONTROL_CODE = 0x1f;
const DELETE_CODE = 0x7f;

/**
 * Control characters have no place in a name or a category code, and they lead
 * the DDE payload variants. Rejecting the whole range at input covers the tab
 * and carriage-return triggers without enumerating them.
 */
function hasControlCharacter(value: string): boolean {
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);

    if (code <= LAST_C0_CONTROL_CODE || code === DELETE_CODE) {
      return true;
    }
  }

  return false;
}

/** Prefix that forces a spreadsheet application to read the cell as literal text. */
const CSV_TEXT_PREFIX = "'";

/** RFC 4180 record separator. */
const CSV_ROW_SEPARATOR = '\r\n';

/**
 * UTF-8 byte order mark. Excel needs it to read an exported `.csv` as UTF-8;
 * without it, accented names ("José") render as mojibake.
 */
export const CSV_UTF8_BOM = '﻿';

/**
 * Does this value read as a spreadsheet formula at an input boundary?
 *
 * Used by input validation to reject a submission outright. Values are never
 * rewritten on the way in — rewriting corrupts the stored record and invites
 * double-escaping. See `csvSafeText` in `@access-cms/validation`.
 */
export function hasSpreadsheetFormulaPrefix(value: string): boolean {
  if (value === '') {
    return false;
  }

  if ((CSV_REJECTED_INPUT_PREFIX_CHARS as readonly string[]).includes(value[0])) {
    return true;
  }

  return hasControlCharacter(value);
}

/**
 * Neutralize one value and quote it per RFC 4180.
 *
 * A leading trigger character gets a single-quote prefix (the OWASP-recommended
 * control), which stops the spreadsheet from evaluating the rest of the cell.
 * Verified in Excel: `=1+1` imports as a live formula displaying `2`, while
 * `'=1+1` imports as inert text.
 *
 * The prefix stays VISIBLE in the opened file — a spreadsheet consumes a leading
 * apostrophe on values typed into a cell, but not on values read from a CSV. So
 * a neutralized payload reads as `'=1+1`. That is the accepted trade-off for
 * this control; legitimate data never gains a prefix.
 *
 * Legitimate values are untouched: only the FIRST character is inspected, and
 * the apostrophe itself is never a trigger, so "O'Brien" survives intact.
 *
 * @param value - Raw cell value. `null`/`undefined` become an empty cell.
 * @returns The cell, safe to concatenate into a CSV record.
 */
export function toSafeCsvCell(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }

  const raw = String(value);

  if (raw === '') {
    return '';
  }

  const firstChar = raw[0];
  const isFormulaTrigger = (CSV_FORMULA_TRIGGER_CHARS as readonly string[]).includes(firstChar);

  // Only a formula trigger is prefixed. A value that already begins with an
  // apostrophe is left alone: spreadsheet applications consume a leading
  // apostrophe on values TYPED into a cell, but not on values read from a CSV,
  // so doubling it would show the user a second apostrophe that is not in their
  // data. Verified in Excel — "'t Hart" imports and displays intact.
  const needsTextPrefix = isFormulaTrigger;
  const neutralized = needsTextPrefix ? `${CSV_TEXT_PREFIX}${raw}` : raw;

  // `;` is quoted alongside the comma: spreadsheet locales that use it as the
  // list separator would otherwise split the cell there, promoting the tail to
  // a cell of its own — and a tail like ";=cmd|..." lands as a live formula.
  const needsQuoting = needsTextPrefix || /[",;\n\r\t]/.test(neutralized);

  if (!needsQuoting) {
    return neutralized;
  }

  return `"${neutralized.replace(/"/g, '""')}"`;
}

/**
 * Serialize one record. Every cell goes through {@link toSafeCsvCell}.
 */
export function buildCsvRow(cells: readonly unknown[]): string {
  return cells.map(toSafeCsvCell).join(',');
}

/**
 * Serialize a full CSV document.
 *
 * Headers are serialized through the same path as the data — they are static
 * today, but routing them around the escape would be a trap for whoever makes
 * them dynamic later.
 *
 * @param headers - Column headers, in order.
 * @param rows - Data records, each aligned to `headers`.
 * @param options.withBom - Prepend the UTF-8 BOM so Excel reads the file as
 *   UTF-8. Enable it for files served as a download.
 */
export function buildCsv(
  headers: readonly string[],
  rows: readonly (readonly unknown[])[],
  options: { withBom?: boolean } = {},
): string {
  const document = [buildCsvRow(headers), ...rows.map(buildCsvRow)].join(CSV_ROW_SEPARATOR);

  return options.withBom ? `${CSV_UTF8_BOM}${document}` : document;
}
